import { IFavouriteMutation, spLogTags } from "@eventr/common";
import { createLocalCacheStore } from "../editItinerary/expenses/local-expense-store/local-cache-store";

/**
 * A queued favourite change, tagged with the organisation it was made in. The request base
 * URL resolves from the selected organisation when the request is sent, so a flush skips
 * changes belonging elsewhere and leaves them queued.
 */
interface QueuedFavourite {
  org: string;
  mutation: IFavouriteMutation;
}

/** One queue entry per itinerary entry, so repeated taps collapse rather than pile up. */
const store = createLocalCacheStore<QueuedFavourite>("favourites", (q) => `favourite-${q.org}-${q.mutation.entryId}`);

/** A change rejected this many times is never going to land, so stop carrying it. */
const MAX_RETRIES = 5;

/** Takes the organisation as an argument. Importing the bound store here deepens its import cycle. */
export const favouriteOutbox = {
  queue: async (org: string, mutation: IFavouriteMutation) => {
    await store.save({ org, mutation });
  },

  /** Queued changes for the given organisation, oldest first. */
  pending: async (org: string): Promise<IFavouriteMutation[]> => {
    return (await store.getAll())
      .filter((item) => item.data.org === org)
      .sort((a, b) => a.data.mutation.at.localeCompare(b.data.mutation.at))
      .map((item) => item.data.mutation);
  },

  /** Clears the changes a sync carried and returns whatever is still queued for the organisation. */
  settle: async (org: string, synced: IFavouriteMutation[]): Promise<IFavouriteMutation[]> => {
    for (const mutation of synced) {
      await store.remove(`favourite-${org}-${mutation.entryId}`);
    }

    return favouriteOutbox.pending(org);
  },

  /** Counts a failed attempt and drops anything that has failed too often. */
  recordFailure: async (org: string) => {
    for (const item of await store.getAll()) {
      if (item.data.org !== org) continue;

      if (item.retries + 1 >= MAX_RETRIES) {
        spLogTags(`[favourites] dropping ${item.storeId} after ${item.retries + 1} attempts`, ["favourites"]);
        await store.remove(item.storeId);
        continue;
      }

      await store.incrementRetry(item);
    }
  },

  clear: async () => {
    for (const item of await store.getAll()) {
      await store.remove(item.storeId);
    }
  },
};
